import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';

const FeedbackContext = createContext(null);

const defaultConfirm = {
  open: false,
  title: '',
  message: '',
  confirmLabel: 'Confirm',
  danger: false,
  requireText: false,
  textLabel: 'Reason',
  resolve: null,
};

export const FeedbackProvider = ({ children }) => {
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [confirmState, setConfirmState] = useState(defaultConfirm);
  const [promptValue, setPromptValue] = useState('');

  const notify = useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const askUser = useCallback((options) => {
    const message = typeof options === 'string' ? options : options?.message;
    return new Promise((resolve) => {
      setPromptValue('');
      setConfirmState({
        open: true,
        title: options?.title || 'Please confirm',
        message,
        confirmLabel: options?.confirmLabel || 'Confirm',
        danger: Boolean(options?.danger),
        requireText: Boolean(options?.requireText),
        textLabel: options?.textLabel || 'Reason',
        resolve,
      });
    });
  }, []);

  const prompt = useCallback(
    (options) =>
      askUser({
        ...(typeof options === 'string' ? { message: options } : options),
        requireText: true,
        confirmLabel: options?.confirmLabel || 'Continue',
      }),
    [askUser]
  );

  useEffect(() => {
    const onNotify = (event) => notify(event.detail?.message, event.detail?.severity || 'info');
    const onConfirm = async (event) => {
      const result = await askUser(event.detail?.options);
      event.detail?.resolve?.(result);
    };
    window.addEventListener('ezpicra:notify', onNotify);
    window.addEventListener('ezpicra:confirm', onConfirm);
    return () => {
      window.removeEventListener('ezpicra:notify', onNotify);
      window.removeEventListener('ezpicra:confirm', onConfirm);
    };
  }, [notify, askUser]);

  const closeConfirm = (result) => {
    confirmState.resolve?.(result);
    setConfirmState(defaultConfirm);
    setPromptValue('');
  };

  const value = useMemo(
    () => ({ notify, askUser, prompt, notifyError: (m) => notify(m, 'error'), notifySuccess: (m) => notify(m, 'success') }),
    [notify, askUser, prompt]
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={confirmState.open}
        onClose={() => closeConfirm(confirmState.requireText ? null : false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">{confirmState.title}</DialogTitle>
        <DialogContent>
          {confirmState.message && (
            <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>{confirmState.message}</DialogContentText>
          )}
          {confirmState.requireText && (
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label={confirmState.textLabel}
              value={promptValue}
              onChange={(event) => setPromptValue(event.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeConfirm(confirmState.requireText ? null : false)}>Cancel</Button>
          <Button
            color={confirmState.danger ? 'error' : 'primary'}
            variant="contained"
            disabled={confirmState.requireText && !promptValue.trim()}
            onClick={() => closeConfirm(confirmState.requireText ? promptValue.trim() : true)}
          >
            {confirmState.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </FeedbackContext.Provider>
  );
};

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used inside FeedbackProvider');
  }
  return context;
};
